import { useState, useEffect, useRef, useCallback } from 'react';
import { Play, Pause, Volume2, VolumeX, Home, X, RotateCcw } from 'lucide-react';
import { playTibetanBowl, playWimhofInhale, playWimhofExhale, playWimhofSlowBreath, speakCountdown, speakInstruction } from '@/utils/audioUtils';
import ExerciseCopyright from './ExerciseCopyright';
import ExerciseTopLogo from './ExerciseTopLogo';

// Duration configurations
const DURATION_CONFIGS = {
  5: {
    label: "5 min",
    phases: [
      { duration: 120, speedMultiplier: 1, label: "Lent" },
      { duration: 120, speedMultiplier: 1.5, label: "Moyen" },
      { duration: 60, speedMultiplier: 2.5, label: "Rapide" }
    ],
    retentionTime: 105,
    totalMinutes: 5
  },
  10: {
    label: "10 min",
    phases: [
      { duration: 240, speedMultiplier: 1, label: "Lent" },
      { duration: 240, speedMultiplier: 1.5, label: "Moyen" },
      { duration: 120, speedMultiplier: 2.5, label: "Rapide" }
    ],
    retentionTime: 120,
    totalMinutes: 10
  },
  15: {
    label: "15 min",
    phases: [
      { duration: 360, speedMultiplier: 1, label: "Lent" },
      { duration: 300, speedMultiplier: 1.5, label: "Moyen" },
      { duration: 240, speedMultiplier: 2.5, label: "Rapide" }
    ],
    retentionTime: 150,
    totalMinutes: 15
  },
  20: {
    label: "20 min",
    phases: [
      { duration: 420, speedMultiplier: 1, label: "Lent" },
      { duration: 360, speedMultiplier: 1.5, label: "Moyen" },
      { duration: 300, speedMultiplier: 2.5, label: "Rapide" },
      { duration: 120, speedMultiplier: 3, label: "Très rapide" }
    ],
    retentionTime: 165,
    totalMinutes: 20
  }
};

const BASE_INHALE = 2.5;
const BASE_EXHALE = 3.5;

const ExtremeBreathingModal = ({ isOpen, onClose, onComplete }) => {
  const [selectedDuration, setSelectedDuration] = useState(5);
  const [totalCycles, setTotalCycles] = useState(1);
  const [soundEnabled, setSoundEnabled] = useState(true);
  
  const [phase, setPhase] = useState('ready');
  const [isActive, setIsActive] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentCycle, setCurrentCycle] = useState(1);
  const [currentPhaseIndex, setCurrentPhaseIndex] = useState(0);
  const [phaseElapsed, setPhaseElapsed] = useState(0);
  const [totalElapsed, setTotalElapsed] = useState(0);
  const [breathPhase, setBreathPhase] = useState('inhale');
  const [fillLevel, setFillLevel] = useState(0);
  const [preCountdown, setPreCountdown] = useState(3);
  const [retentionTime, setRetentionTime] = useState(0);
  const [recoveryTime, setRecoveryTime] = useState(45);
  const [finalInhaleTime, setFinalInhaleTime] = useState(3);
  
  const soundEnabledRef = useRef(true);
  const phaseRef = useRef('ready');
  const currentSpeedRef = useRef(1);
  const phaseElapsedRef = useRef(0);
  
  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
  }, [soundEnabled]);
  
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  useEffect(() => {
    phaseElapsedRef.current = phaseElapsed;
  }, [phaseElapsed]);

  const config = DURATION_CONFIGS[selectedDuration];

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getCurrentPhaseInfo = useCallback(() => {
    if (!config) return null;
    return config.phases[currentPhaseIndex] || null;
  }, [config, currentPhaseIndex]);

  const getTimings = useCallback((speedMultiplier) => {
    const inhale = BASE_INHALE / speedMultiplier;
    const exhale = BASE_EXHALE / speedMultiplier;
    const hold = 0.3 / speedMultiplier;
    return { inhale, exhale, hold, total: inhale + hold + exhale };
  }, []);

  // Pre-countdown effect
  useEffect(() => {
    if (phase !== 'pre-countdown' || isPaused) return;
    
    const interval = setInterval(() => {
      setPreCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          setPhase('breathing');
          setBreathPhase('inhale');
          setPhaseElapsed(0);
          phaseElapsedRef.current = 0;
          setCurrentPhaseIndex(0);
          currentSpeedRef.current = 1;
          if (soundEnabledRef.current) {
            playWimhofSlowBreath(0.7);
          }
          return 3;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(interval);
  }, [phase, isPaused]);

  // Main breathing effect
  useEffect(() => {
    if (phase !== 'breathing' || isPaused || !config) return;
    
    const currentPhase = config.phases[currentPhaseIndex];
    if (!currentPhase) return;
    
    const timings = getTimings(currentPhase.speedMultiplier);
    currentSpeedRef.current = currentPhase.speedMultiplier;
    
    let cycleStart = Date.now();
    let currentBreathPhase = 'inhale';
    
    const interval = setInterval(() => {
      const now = Date.now();
      const cycleElapsed = (now - cycleStart) / 1000;
      
      setPhaseElapsed(prev => {
        const newVal = prev + 0.05;
        phaseElapsedRef.current = newVal;
        return newVal;
      });
      setTotalElapsed(prev => prev + 0.05);
      
      // Check if current phase is complete
      if (phaseElapsedRef.current >= currentPhase.duration) {
        if (currentPhaseIndex < config.phases.length - 1) {
          setCurrentPhaseIndex(prev => prev + 1);
          setPhaseElapsed(0);
          phaseElapsedRef.current = 0;
          if (soundEnabledRef.current) {
            const nextPhase = config.phases[currentPhaseIndex + 1];
            speakCountdown(nextPhase.label, 'fr-FR');
          }
        } else {
          clearInterval(interval);
          setPhase('final-inhale');
          setFinalInhaleTime(3);
          if (soundEnabledRef.current) {
            speakCountdown('Grande inspiration', 'fr-FR');
            playWimhofSlowBreath(0.7);
          }
        }
        return;
      }
      
      // Breathing animation
      if (cycleElapsed <= timings.inhale) {
        const progress = cycleElapsed / timings.inhale;
        setFillLevel(progress * 100);
        if (currentBreathPhase !== 'inhale') {
          currentBreathPhase = 'inhale';
          setBreathPhase('inhale');
        }
      } else if (cycleElapsed <= timings.inhale + timings.hold) {
        setFillLevel(100);
      } else if (cycleElapsed <= timings.total) {
        const exhaleProgress = (cycleElapsed - timings.inhale - timings.hold) / timings.exhale;
        setFillLevel(100 - (exhaleProgress * 80));
        if (currentBreathPhase !== 'exhale') {
          currentBreathPhase = 'exhale';
          setBreathPhase('exhale');
          if (soundEnabledRef.current && currentPhase.speedMultiplier < 2) {
            playWimhofExhale(0.5);
          }
        }
      } else {
        cycleStart = now;
        currentBreathPhase = 'inhale';
        setBreathPhase('inhale');
        if (soundEnabledRef.current) {
          if (currentPhase.speedMultiplier < 2) {
            playWimhofSlowBreath(0.6);
          } else {
            playWimhofInhale(0.4);
          }
        }
      }
    }, 50);
    
    return () => clearInterval(interval);
  }, [phase, isPaused, config, currentPhaseIndex, getTimings]);

  // Final inhale effect
  useEffect(() => {
    if (phase !== 'final-inhale' || isPaused) return;
    
    const interval = setInterval(() => {
      setFinalInhaleTime(prev => {
        if (prev <= 0) {
          clearInterval(interval);
          setPhase('retention');
          setRetentionTime(config.retentionTime);
          setFillLevel(100);
          if (soundEnabledRef.current) {
            speakCountdown('Apnée', 'fr-FR');
            playTibetanBowl('low');
          }
          return 0;
        }
        setFillLevel(((3 - prev) / 3) * 100);
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(interval);
  }, [phase, isPaused, config]);

  // Retention effect
  useEffect(() => {
    if (phase !== 'retention' || isPaused) return;
    
    const interval = setInterval(() => {
      setRetentionTime(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          if (currentCycle < totalCycles) {
            setPhase('recovery');
            setRecoveryTime(45);
            if (soundEnabledRef.current) {
              playTibetanBowl('high');
              speakInstruction('Récupération', 'fr-FR');
            }
          } else {
            setPhase('complete');
            if (soundEnabledRef.current) {
              playTibetanBowl('high');
              speakInstruction('Terminé', 'fr-FR');
            }
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(interval);
  }, [phase, isPaused, currentCycle, totalCycles]);

  // Recovery effect
  useEffect(() => {
    if (phase !== 'recovery' || isPaused) return;
    
    const interval = setInterval(() => {
      setRecoveryTime(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          setCurrentCycle(c => c + 1);
          setPhase('pre-countdown');
          setPreCountdown(3);
          setCurrentPhaseIndex(0);
          setPhaseElapsed(0);
          phaseElapsedRef.current = 0;
          setFillLevel(0);
          return 45;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(interval);
  }, [phase, isPaused]);

  const startSession = () => {
    setIsActive(true);
    setIsPaused(false);
    setPhase('pre-countdown');
    setPreCountdown(3);
    setCurrentCycle(1);
    setCurrentPhaseIndex(0);
    setPhaseElapsed(0);
    phaseElapsedRef.current = 0;
    setTotalElapsed(0);
    setFillLevel(0);
    setRetentionTime(0);
    setRecoveryTime(45);
  };

  const resetSession = () => {
    setIsActive(false);
    setIsPaused(false);
    setPhase('ready');
    setCurrentCycle(1);
    setCurrentPhaseIndex(0);
    setPhaseElapsed(0);
    phaseElapsedRef.current = 0;
    setTotalElapsed(0);
    setFillLevel(0);
    setBreathPhase('inhale');
    setRetentionTime(0);
    setRecoveryTime(45);
    setPreCountdown(3);
    setFinalInhaleTime(3);
  };

  const togglePause = () => {
    setIsPaused(!isPaused);
  };

  const handleClose = () => {
    resetSession();
    onClose();
  };

  const handleComplete = () => {
    if (onComplete) {
      onComplete({
        exercise: 'extreme',
        duration: selectedDuration,
        cycles: currentCycle,
        totalTime: totalElapsed
      });
    }
    resetSession();
    onClose();
  };

  if (!isOpen) return null;

  const currentPhaseInfo = getCurrentPhaseInfo();
  const phaseProgress = currentPhaseInfo ? (phaseElapsed / currentPhaseInfo.duration) * 100 : 0;

  return (
    <div 
      className="fixed inset-0 z-[60] flex items-center justify-center overflow-hidden"
      style={{ 
        backgroundImage: 'url(https://images.unsplash.com/photo-1600582910964-5b7c109e6868?w=1920&q=80)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <ExerciseCopyright />
      {/* Dark overlay for better text readability */}
      <div className="absolute inset-0 bg-black/30" />
      {/* Navigation Buttons */}
      <div className="absolute top-4 left-4 z-20">
        <button
          onClick={handleClose}
          className="p-3 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-full transition-colors"
          title="Retour aux exercices"
        >
          <Home size={20} className="text-white" />
        </button>
      </div>
      
      <div className="absolute top-4 right-4 z-20 flex gap-2">
        {isActive && (
          <button
            onClick={resetSession}
            className="p-3 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-full transition-colors"
            title="Recommencer"
          >
            <RotateCcw size={20} className="text-white" />
          </button>
        )}
        <button
          onClick={() => setSoundEnabled(!soundEnabled)}
          className="p-3 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-full transition-colors"
          title={soundEnabled ? "Couper le son" : "Activer le son"}
        >
          {soundEnabled ? <Volume2 size={20} className="text-white" /> : <VolumeX size={20} className="text-white" />}
        </button>
        <button
          onClick={handleClose}
          className="p-3 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-full transition-colors"
          title="Fermer"
        >
          <X size={20} className="text-white" />
        </button>
      </div>

      <ExerciseTopLogo />

      {/* Timer Info - Below the Play/Pause buttons on mobile, top center on desktop */}
      {phase !== 'ready' && phase !== 'complete' && (
        <div className="absolute bottom-16 left-1/2 transform -translate-x-1/2 z-20 max-w-[85%] lg:bottom-auto lg:top-4 lg:max-w-none">
          <div className="bg-white/20 backdrop-blur-md rounded-2xl px-6 py-2 text-center">
            {phase === 'breathing' && currentPhaseInfo && (
              <div className="flex flex-col items-center">
                <span className="text-white/80 text-sm font-medium">Phase: {currentPhaseInfo.label} ({currentPhaseInfo.speedMultiplier}x)</span>
                <span className="text-white font-bold text-xl">{formatTime(Math.floor(phaseElapsed))} / {formatTime(currentPhaseInfo.duration)}</span>
              </div>
            )}
            {phase === 'retention' && (
              <span className="text-white font-bold text-2xl">Apnée: {formatTime(retentionTime)}</span>
            )}
            {phase === 'recovery' && (
              <span className="text-white font-bold text-xl">Récupération: {recoveryTime}s</span>
            )}
            {phase === 'pre-countdown' && (
              <span className="text-white font-bold text-lg">Préparez-vous...</span>
            )}
            {phase === 'final-inhale' && (
              <span className="text-white font-bold text-lg">Grande inspiration</span>
            )}
          </div>
        </div>
      )}

      {/* Warning Banner - visible only in ready state */}
      {phase === 'ready' && (
        <div className="absolute top-20 left-1/2 transform -translate-x-1/2 z-20 w-full max-w-2xl px-4">
          <div className="bg-amber-500/90 backdrop-blur-md rounded-xl px-4 py-3 text-center border border-amber-400/50 shadow-lg">
            <p className="text-amber-950 text-sm font-medium">
              ⚠️ <strong>Attention, pratique avancée</strong> — Ne PAS pratiquer si vous avez des problèmes cardiaques, de l&apos;asthme, une BPCO, de l&apos;hypertension... et surtout sans l&apos;avoir pratiquée au préalable avec un professionnel.
            </p>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className={`relative z-10 w-full h-full overflow-y-auto px-4 pb-8 flex flex-col lg:flex-row lg:items-center lg:justify-center lg:pb-8 ${phase === 'ready' ? 'pt-36 sm:pt-40' : 'pt-20 sm:pt-24'}`}>
        <div className="flex flex-col items-center my-auto gap-6 lg:grid lg:grid-cols-[1fr_auto_1fr] lg:gap-10 lg:items-center w-full">
          
          {/* Left Panel - Settings */}
          <div className="w-full max-w-xs lg:w-72 flex-shrink-0 order-3 lg:order-none lg:col-start-1 lg:row-start-1 lg:justify-self-end">
            {(!isActive || isPaused || phase === 'complete') && (
              <div className="bg-white/90 backdrop-blur rounded-2xl px-4 sm:px-6 py-4 sm:py-5 shadow-lg">
                {phase === 'ready' && (
                  <>
                    <h3 className="text-slate-700 font-semibold text-sm mb-3">Durée hyperventilation</h3>
                    <div className="grid grid-cols-4 gap-2 mb-4">
                      {Object.entries(DURATION_CONFIGS).map(([key, cfg]) => (
                        <button
                          key={key}
                          onClick={() => setSelectedDuration(Number(key))}
                          className={`py-2 rounded-xl text-xs font-medium transition-all ${
                            selectedDuration === Number(key)
                              ? 'bg-teal-500 text-white'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {cfg.label}
                        </button>
                      ))}
                    </div>

                    <h3 className="text-slate-700 font-semibold text-sm mb-3">Nombre de cycles</h3>
                    <div className="grid grid-cols-3 gap-2 mb-4">
                      {[1, 2, 3].map(num => (
                        <button
                          key={num}
                          onClick={() => setTotalCycles(num)}
                          className={`py-2 rounded-xl text-xs font-medium transition-all ${
                            totalCycles === num
                              ? 'bg-teal-500 text-white'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {num} cycle{num > 1 ? 's' : ''}
                        </button>
                      ))}
                    </div>

                    <div className="bg-slate-100 rounded-xl p-3 text-xs">
                      <p className="text-slate-500 mb-2">Phases:</p>
                      {config.phases.map((p, idx) => (
                        <div key={idx} className="flex justify-between text-slate-600">
                          <span>{p.label} ({p.speedMultiplier}x)</span>
                          <span>{formatTime(p.duration)}</span>
                        </div>
                      ))}
                      <div className="border-t border-slate-200 mt-2 pt-2">
                        <div className="flex justify-between text-teal-600 font-medium">
                          <span>Apnée cible</span>
                          <span>{formatTime(config.retentionTime)}</span>
                        </div>
                      </div>
                    </div>
                  </>
                )}
                
                {phase === 'complete' && (
                  <div className="text-center">
                    <p className="text-green-600 font-bold text-lg mb-2">Session terminée !</p>
                    <p className="text-slate-600 text-sm">
                      {totalCycles} cycle{totalCycles > 1 ? 's' : ''} × {selectedDuration} min
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Center - Capsule Animation */}
          <div className="flex flex-col items-center order-1 lg:order-none lg:col-start-2 lg:row-start-1">
            <div className="relative w-48 h-64 sm:w-56 sm:h-72 lg:w-64 lg:h-80">
              {/* Capsule container */}
              <div 
                className="absolute inset-0 rounded-full overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 100%)',
                  boxShadow: 'inset 0 4px 20px rgba(0,0,0,0.2), 0 8px 32px rgba(0,0,0,0.3)',
                  border: '2px solid rgba(255,255,255,0.2)'
                }}
              >
                {/* Liquid fill */}
                <div 
                  className="absolute bottom-0 left-0 right-0 transition-all duration-150 ease-out"
                  style={{ 
                    height: `${fillLevel}%`,
                    background: phase === 'retention' 
                      ? 'linear-gradient(to top, rgba(20,184,166,0.9) 0%, rgba(45,212,191,0.7) 50%, rgba(94,234,212,0.5) 100%)'
                      : phase === 'recovery'
                      ? 'linear-gradient(to top, rgba(34,197,94,0.9) 0%, rgba(74,222,128,0.7) 50%, rgba(134,239,172,0.5) 100%)'
                      : 'linear-gradient(to top, rgba(6,182,212,0.9) 0%, rgba(34,211,238,0.7) 50%, rgba(103,232,249,0.5) 100%)',
                    boxShadow: 'inset 0 10px 30px rgba(255,255,255,0.3)'
                  }}
                />
                
                {/* Center content */}
                <div className="absolute inset-0 flex flex-col items-center justify-center z-10 text-center px-4">
                  {phase === 'ready' && (
                    <span className="text-white/80 text-lg font-medium">Prêt</span>
                  )}
                  {phase === 'pre-countdown' && (
                    <span className="text-6xl font-bold text-white animate-pulse">{preCountdown}</span>
                  )}
                  {phase === 'breathing' && (
                    <>
                      <span className="text-xl font-semibold text-white capitalize">
                        {breathPhase === 'inhale' ? 'Inspirez' : 'Expirez'}
                      </span>
                      <span className="text-white/60 text-sm mt-1">{currentPhaseInfo?.speedMultiplier}x</span>
                    </>
                  )}
                  {phase === 'final-inhale' && (
                    <>
                      <span className="text-lg font-medium text-white">Inspirez</span>
                      <span className="text-5xl font-bold text-white">{finalInhaleTime}</span>
                    </>
                  )}
                  {phase === 'retention' && (
                    <>
                      <span className="text-sm text-white/70 uppercase">Apnée</span>
                      <span className="text-4xl font-bold text-white">{formatTime(retentionTime)}</span>
                    </>
                  )}
                  {phase === 'recovery' && (
                    <>
                      <span className="text-sm text-white/70 uppercase">Récupération</span>
                      <span className="text-4xl font-bold text-white">{recoveryTime}s</span>
                    </>
                  )}
                  {phase === 'complete' && (
                    <span className="text-2xl font-bold text-white">Terminé !</span>
                  )}
                </div>
              </div>
            </div>
            
            {/* Cycle indicator */}
            {isActive && totalCycles > 1 && (
              <p className="text-white/70 text-lg mt-4">
                Cycle {currentCycle} / {totalCycles}
              </p>
            )}
            
            {/* Phase indicators */}
            {phase === 'breathing' && config && (
              <div className="flex gap-3 mt-6">
                {config.phases.map((p, idx) => (
                  <div 
                    key={idx}
                    className={`px-5 py-2 rounded-full text-base font-medium transition-all ${
                      idx === currentPhaseIndex 
                        ? 'bg-white text-teal-700 font-bold' 
                        : idx < currentPhaseIndex
                        ? 'bg-white/40 text-white'
                        : 'bg-white/10 text-white/50'
                    }`}
                  >
                    {p.label}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right Panel - Controls */}
          <div className="w-full max-w-xs lg:w-72 flex-shrink-0 order-2 lg:order-none lg:col-start-3 lg:row-start-1 lg:justify-self-start">
            <div className="bg-white/90 backdrop-blur rounded-2xl px-4 sm:px-6 py-4 sm:py-5 shadow-lg">
              {phase === 'ready' && (
                <button
                  onClick={startSession}
                  className="w-full py-4 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-semibold text-lg hover:from-teal-600 hover:to-cyan-600 transition-all flex items-center justify-center gap-2"
                >
                  <Play size={24} />
                  Commencer
                </button>
              )}
              
              {isActive && phase !== 'ready' && phase !== 'complete' && (
                <div className="space-y-3">
                  <button
                    onClick={togglePause}
                    className={`w-full py-4 rounded-xl font-semibold text-lg transition-all flex items-center justify-center gap-2 ${
                      isPaused 
                        ? 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white' 
                        : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                    }`}
                  >
                    {isPaused ? <Play size={24} /> : <Pause size={24} />}
                    {isPaused ? 'Reprendre' : 'Pause'}
                  </button>
                  <button
                    onClick={resetSession}
                    className="w-full py-3 rounded-xl bg-slate-100 text-slate-600 font-medium hover:bg-slate-200 transition-all flex items-center justify-center gap-2"
                  >
                    <RotateCcw size={20} />
                    Recommencer
                  </button>
                </div>
              )}
              
              {phase === 'complete' && (
                <button
                  onClick={handleComplete}
                  className="w-full py-4 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold text-lg hover:from-green-600 hover:to-emerald-600 transition-all"
                >
                  Terminer
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExtremeBreathingModal;
